import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";

// Roles available depending on who is creating the user.
const ROLE_OPTIONS = {
  SUPER_ADMIN: [
    "SUPER_ADMIN",
    "CORP_ADMIN",
    "ZONAL_AC",
    "DEPT_HEAD",
    "OFFICER",
    "AUDITOR",
    "VIEWER",
  ],
  CORP_ADMIN: [
    "CORP_ADMIN",
    "ZONAL_AC",
    "DEPT_HEAD",
    "OFFICER",
    "AUDITOR",
    "VIEWER",
  ],
};

const ROLE_LABELS = {
  SUPER_ADMIN: "superAdmin",
  CORP_ADMIN: "corpAdmin",
  ZONAL_AC: "zonalAcLabel",
  DEPT_HEAD: "deptHeadLabel",
  OFFICER: "officerLabel",
  AUDITOR: "auditorLabel",
  VIEWER: "viewerLabel",
};

export default function AddUser() {
  const { user: me } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  // CUG-account creators can only add officers. Their zone / department is
  // locked to their own. Platform admins see the full role list.
  const isZoneCug = me?.role === "ZONAL_AC" && me?.is_zone_account;
  const isDeptCug = me?.role === "DEPT_HEAD" && me?.is_department_account;
  const isLockedToOfficer = isZoneCug || isDeptCug;

  const availableRoles = isLockedToOfficer
    ? ["OFFICER"]
    : ROLE_OPTIONS[me?.role] || [];

  const [form, setForm] = useState({
    mobile: "",
    full_name: "",
    role: isLockedToOfficer ? "OFFICER" : "",
    designation: "",
    department: isDeptCug ? me?.department || "" : "",
    zone: isZoneCug ? String(me?.zone || "") : "",
    password: "",
    cug_number: "",
    number_type: "personal",
  });
  const [zones, setZones] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!isLockedToOfficer) {
      api.get("/zones/", { params: { page_size: 100 } })
        .then((r) => setZones(r.data.results || r.data))
        .catch(() => {});
      api.get("/departments/", { params: { page_size: 100 } })
        .then((r) => setDepartments(r.data.results || r.data))
        .catch(() => {});
    }
  }, [isLockedToOfficer]);

  // Pre-fill the CUG field with the selected zone/department's current CUG
  // so the admin can see and update it. Only relevant for ZONAL_AC/DEPT_HEAD.
  useEffect(() => {
    if (form.role === "ZONAL_AC" && form.zone) {
      const z = zones.find((x) => String(x.id) === String(form.zone));
      setForm((prev) => ({ ...prev, cug_number: z?.cug_number || "" }));
    } else if (form.role === "DEPT_HEAD" && form.department) {
      const d = departments.find((x) => x.name === form.department);
      setForm((prev) => ({ ...prev, cug_number: d?.cug_number || "" }));
    } else if (!["ZONAL_AC", "DEPT_HEAD"].includes(form.role)) {
      setForm((prev) => ({ ...prev, cug_number: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.role, form.zone, form.department, zones, departments]);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.role) {
      setError(t("errorRoleRequired"));
      return;
    }

    const mobile = form.role === "DEPT_HEAD" ? "" : form.mobile.trim();
    const fullName = form.role === "DEPT_HEAD" ? "" : form.full_name.trim();
    const cug = form.cug_number.trim();
    const isAcOrHod = ["ZONAL_AC", "DEPT_HEAD"].includes(form.role);

    // For AC / HOD, the CUG admin desk is the permanent account — the
    // personal user is optional (set only when a real person is in post).
    // For every other role, mobile + name are required as before.
    const skipPersonal = isAcOrHod && !mobile && !fullName;
    if (!skipPersonal) {
      if (!mobile || !fullName) {
        setError(t("errorPersonalRequired"));
        return;
      }
      if (!form.password.trim()) {
        setError(t("errorPasswordRequired"));
        return;
      }
      if (form.password.trim().length < 6) {
        setError(t("errorPasswordLength"));
        return;
      }
    }
    if (isAcOrHod && !cug) {
      setError(t("errorCugRequired"));
      return;
    }

    setBusy(true);
    try {
      let userCreated = false;
      if (!skipPersonal) {
        const payload = {
          mobile,
          full_name: fullName,
          role: form.role,
          designation: form.designation.trim() || undefined,
          department: form.department || undefined,
          zone: form.zone ? Number(form.zone) : undefined,
          password: form.password.trim() || undefined,
          number_type: form.number_type,
        };
        Object.keys(payload).forEach(
          (k) => payload[k] === undefined && delete payload[k]
        );
        await api.post("/auth/users/", payload);
        userCreated = true;
      }

      // If a CUG number was provided for an AC / HOD, apply it to the
      // selected zone or department. The backend save() hook then
      // auto-creates the permanent CUG admin account.
      const cug = form.cug_number.trim();
      let cugMsg = "";
      if (cug && form.role === "ZONAL_AC" && form.zone) {
        try {
          await api.patch(`/zones/${form.zone}/`, { cug_number: cug });
          cugMsg = ` Zone CUG set to ${cug} (admin account auto-created).`;
        } catch {
          cugMsg = " (Could not update zone CUG — check permissions.)";
        }
      } else if (cug && form.role === "DEPT_HEAD" && form.department) {
        try {
          const dept = departments.find((d) => d.name === form.department);
          if (dept) {
            await api.patch(`/departments/${dept.id}/`, { cug_number: cug });
            cugMsg = ` Department CUG set to ${cug} (admin account auto-created).`;
          }
        } catch {
          cugMsg = " (Could not update department CUG — check permissions.)";
        }
      }

      const personalMsg = userCreated
        ? t("personalUserCreated") + ` (login: ${mobile}, password: ${form.password.trim() || "audit@123"}).`
        : t("noPersonalUserCreated");
      setSuccess(`${personalMsg}${cugMsg}`);
      setForm((prev) => ({
        ...prev,
        mobile: "",
        full_name: "",
        role: isLockedToOfficer ? "OFFICER" : "",
        designation: "",
        password: "",
        cug_number: "",
        number_type: "personal",
      }));
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.detail ||
        (typeof data === "object"
          ? Object.entries(data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
              .join("\n")
          : null) ||
        "Could not create user. Please try again.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  // Only render fields that are relevant for the chosen role
  const showZone = ["ZONAL_AC", "OFFICER"].includes(form.role);
  const showDepartment = ["DEPT_HEAD", "OFFICER"].includes(form.role);
  // CUG field appears when creating a Zonal AC or Dept Head. It updates the
  // selected zone's / department's CUG number, which auto-creates the CUG
  // admin account via the Zone/Department save() hook.
  const showCug = ["ZONAL_AC", "DEPT_HEAD"].includes(form.role);

  return (
    <div className="page-add-user" style={{ maxWidth: 720, width: "100%", margin: "0 auto", alignSelf: "center" }}>

      <div className="card">
        {(isZoneCug || isDeptCug) && (
          <div
            className="muted"
            style={{
              background: "#f0f9ff",
              border: "1px solid #7dd3fc",
              borderRadius: 6,
              padding: "10px 12px",
              marginBottom: 14,
              color: "#075985",
              fontSize: 13,
            }}
          >
            {isZoneCug ? t("zoneLockedAc") : t("deptLockedHod")}
          </div>
        )}

        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fca5a5",
              color: "#991b1b",
              padding: "10px 12px",
              borderRadius: 6,
              marginBottom: 14,
              fontSize: 13,
              whiteSpace: "pre-wrap",
            }}
          >
            {error}
          </div>
        )}
        {success && (
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #86efac",
              color: "#166534",
              padding: "10px 12px",
              borderRadius: 6,
              marginBottom: 14,
              fontSize: 13,
            }}
          >
            {success}
          </div>
        )}

        <form onSubmit={submit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            {form.role !== "DEPT_HEAD" && (
              <>
                <div className="field">
                  <label>
                    Mobile / CUG Number
                    {form.role === "ZONAL_AC" ? t("optional") : " *"}
                  </label>
                  <input
                    value={form.mobile}
                    onChange={update("mobile")}
                    placeholder={t("mobilePlaceholder")}
                    maxLength={15}
                    required={form.role !== "ZONAL_AC"}
                  />
                </div>
                <div className="field">
                  <label>Number Type *</label>
                  <select value={form.number_type} onChange={update("number_type")}>
                    <option value="personal">Personal Number</option>
                    <option value="cug">CUG Number</option>
                  </select>
                </div>
                <div className="field">
                  <label>
                    {t("fullName")}
                    {form.role === "ZONAL_AC" ? t("optional") : " *"}
                  </label>
                  <input
                    value={form.full_name}
                    onChange={update("full_name")}
                    placeholder={t("fullNamePlaceholder")}
                    required={form.role !== "ZONAL_AC"}
                  />
                </div>
              </>
            )}

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>{t("role")} *</label>
              <select
                value={form.role}
                onChange={update("role")}
                disabled={isLockedToOfficer}
              >
                {!isLockedToOfficer && (
                  <option value="">{t("selectUserType")}</option>
                )}
                {availableRoles.map((r) => (
                  <option key={r} value={r}>
                    {t(ROLE_LABELS[r])}
                  </option>
                ))}
              </select>
            </div>

            {showZone && (
              <div className="field">
                <label>{t("zone")} {form.role === "OFFICER" ? "" : t("optional")}</label>
                <select
                  value={form.zone}
                  onChange={update("zone")}
                  disabled={isZoneCug}
                >
                  <option value="">{t("selectZonePrompt")}</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name} ({z.code})
                    </option>
                  ))}
                  {/* When locked for zone CUG, ensure the locked zone is in the list */}
                  {isZoneCug && zones.length === 0 && (
                    <option value={String(me?.zone || "")}>
                      {t("yourZone")}
                    </option>
                  )}
                </select>
              </div>
            )}

            {showDepartment && (
              <div className="field">
                <label>{t("department")}</label>
                <select
                  value={form.department}
                  onChange={update("department")}
                  disabled={isDeptCug}
                >
                  <option value="">{t("selectDeptPrompt")}</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                  {isDeptCug && departments.length === 0 && (
                    <option value={me?.department || ""}>
                      {t("yourDept")}
                    </option>
                  )}
                </select>
              </div>
            )}

            {showCug && (
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label>
                  {t("cugNumber")}{" "}
                  <span className="muted" style={{ fontWeight: "normal", fontSize: 11 }}>
                    ({t("cugHint")})
                  </span>
                </label>
                <input
                  value={form.cug_number}
                  onChange={update("cug_number")}
                  placeholder="e.g. 9442010001"
                  maxLength={15}
                />
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  {form.role === "ZONAL_AC" ? t("cugDesc") : t("cugDescDept")}
                  {form.cug_number && (form.role === "ZONAL_AC"
                    ? zones.find((z) => String(z.id) === String(form.zone))?.cug_number
                    : departments.find((d) => d.name === form.department)?.cug_number)
                    ? t("existingCugShown")
                    : ""}
                </div>
              </div>
            )}

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>{t("designation")}{t("optional")}</label>
              <input
                value={form.designation}
                onChange={update("designation")}
                placeholder={t("designationPlaceholder")}
              />
            </div>

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>
                {t("password")}
                {(!["ZONAL_AC", "DEPT_HEAD"].includes(form.role) || !!form.mobile || !!form.full_name) ? " *" : t("optional")}
              </label>
              <input
                type="text"
                value={form.password}
                onChange={update("password")}
                placeholder="Minimum 6 characters"
                required={!["ZONAL_AC", "DEPT_HEAD"].includes(form.role) || !!form.mobile || !!form.full_name}
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                {t("passwordHint")}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 18,
            }}
          >
            <button
              type="button"
              className="btn"
              onClick={() => navigate(-1)}
              disabled={busy}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy}
            >
              {busy ? t("creating") : t("createUser")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}